import { EM_DASH } from "constants/index";

import { VFC } from "react";

import { CalendarToday } from "@material-ui/icons";
import { Button, Paper, portexColor, TextInput } from "@portex-pro/ui-components";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useGetPartnersRequestQuery } from "api/rest/partners/partnersApi";
import formatAddress from "app/pages/shipments/utils/formatAddress";
import AttachFileFromButtonContainer from "components/file-uploads/AttachFileFromButtonContainer";
import AttachmentsListContainer from "components/file-uploads/AttachmentsListContainer";
import RecipientTags from "components/RecipientTags";
import Text from "components/Text";
import useLDFlag from "hooks/useLDFlag";
import { useUserContext } from "hooks/useUserContext";
import capitalize from "lodash/capitalize";
import { DateTime } from "luxon";
import { FormProvider, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RoutingTypeEnum } from "types/RoutingType";

import { StepBlock } from "../../_components/StepBlock";
import TemplateOption from "../../_components/TemplateOption";
import { useQuoteRequestSelector } from "../../_store";
import { QuoteStepIndex } from "../../types";
import { FinalSchema } from "../_schemas";

const ReviewStep: VFC<{ methods: UseFormReturn<FinalSchema>; onSubmit: (data: FinalSchema) => void }> = ({
  onSubmit,
  methods,
}) => {
  const { t } = useTranslation(["quoteRequest", "common", "shipper"]);
  const { user } = useUserContext();
  const { stepIndex, quoteRequest: quoteRequest } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const releaseQuoteRequestFiles = useLDFlag("releaseQuoteRequestFiles");

  const { data: partnerData } = useGetPartnersRequestQuery(
    !!quoteRequest.partners?.length
      ? {
          queryParams: {
            partnerType: "external",
            ids: quoteRequest.partners?.map((partner) => partner.contact_id) ?? [],
          },
        }
      : skipToken
  );
  const { data: teammatesData } = useGetPartnersRequestQuery(
    !!quoteRequest.teammates?.length
      ? {
          queryParams: {
            partnerType: "internal",
            ids: quoteRequest.teammates?.map((teammate) => teammate.contact_id) ?? [],
          },
        }
      : skipToken
  );

  const indexOffset =
    quoteRequest.load_spec?.routing_type === RoutingTypeEnum.D2D ||
    quoteRequest.load_spec?.routing_type === RoutingTypeEnum.D2P
      ? 0
      : 1;
  const originAirportIndex = 1 - indexOffset;
  const destinationAirportIndex = 2 - indexOffset;
  const destinationIndex = 3 - indexOffset;

  // Routing type conditions
  const hasOriginSection =
    quoteRequest.load_spec?.routing_type === RoutingTypeEnum.D2D ||
    quoteRequest.load_spec?.routing_type === RoutingTypeEnum.D2P;

  const hasDestinationSection =
    quoteRequest.load_spec?.routing_type === RoutingTypeEnum.D2D ||
    quoteRequest.load_spec?.routing_type === RoutingTypeEnum.P2D;

  const submit = (data: FinalSchema) => {
    onSubmit(data);
  };

  if (stepIndex < QuoteStepIndex.Review) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col gap-6 w-full">
        <StepBlock
          title={t("quoteRequest:QuoteRequestPageSteps_review")}
          stepNumber={7}
          footer={
            stepIndex <= QuoteStepIndex.Review ? (
              <div className="flex justify-between mt-2 w-full">
                <div />
                <div className="flex justify-between items-center space-x-3">
                  <TemplateOption />
                  <Button variant="contained" color="primary" size="large" onClick={methods.handleSubmit(submit)}>
                    {t("shipper:sendViaPortex")}
                  </Button>
                </div>
              </div>
            ) : null
          }
        >
          <div className="flex flex-col w-full mt-6">
            {/* Email Preview Section */}
            <div className="mb-6">
              <Paper variant="outlined" className="bg-gray-50 p-4">
                <div className="flex flex-col space-y-3 mb-4">
                  <div className="flex items-center">
                    <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.bcc")}:</span>
                    <RecipientTags
                      recipients={
                        partnerData?.data.partners.map((partner) => partner.company_name ?? partner.user.email) ?? []
                      }
                      limit={3}
                      customStyle={{ backgroundColor: "#F3F4F5" }}
                    />
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.cc")}:</span>
                    <RecipientTags
                      recipients={teammatesData?.data.partners.map((teammate) => teammate.user.email) ?? []}
                      limit={3}
                      customStyle={{ backgroundColor: "#F3F4F5" }}
                    />
                  </div>
                  <div className="flex items-center mb-4">
                    <span className="font-medium text-sm w-20">{t("quoteRequest:ReviewStep.subject")}:</span>
                    <TextInput value={quoteRequest.email_metadata?.subject} fullWidth className="bg-white" />
                  </div>
                </div>

                {!!releaseQuoteRequestFiles && (
                  <div className="flex pb-4 justify-end">
                    <AttachFileFromButtonContainer />
                  </div>
                )}

                <Paper variant="outlined">
                  <div className="flex justify-between p-4 items-center border-b-2">
                    <div className="flex flex-col">
                      <Text size="large" weight="bold">
                        {t("common:quoteRequest")}
                      </Text>
                      <Text size="small" typographyProps={{ color: "textSecondary" }}>
                        {quoteRequest.portex_id}
                      </Text>
                    </div>

                    {quoteRequest.deadline_respond_at ? (
                      <div className="bg-orange-100 text-red-500 py-2 px-2 rounded-[8px]">
                        {t("common:deadlineIn{{time}}", {
                          time: DateTime.fromISO(quoteRequest.deadline_respond_at).toRelative(),
                        })}
                      </div>
                    ) : (
                      <div />
                    )}
                  </div>

                  <div className="flex">
                    <div className="flex flex-col w-1/2 border-r-2 p-4 space-y-4">
                      <Text size="small" weight="bold">
                        {t("shipper:shipmentDetails")}
                      </Text>

                      <div className="flex flex-col space-y-1">
                        <Text size="small" typographyProps={{ color: "textSecondary" }}>
                          {t("common:shipper")}
                        </Text>
                        <Text size="small" weight="bold">
                          {quoteRequest.shipper_name ?? user?.shipper?.name ?? EM_DASH}
                        </Text>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <Text size="small" typographyProps={{ color: "textSecondary" }}>
                          {t("common:mode")}
                        </Text>
                        <Text size="small" weight="bold">
                          {quoteRequest.mode ? t(`common:modeEnum.${quoteRequest.mode}`) : EM_DASH}
                        </Text>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <Text size="small" typographyProps={{ color: "textSecondary" }}>
                          {t("common:totalWeight")}
                        </Text>
                        <Text size="small" weight="bold">
                          {quoteRequest.load_spec?.total_weight
                            ? `${quoteRequest.load_spec.total_weight} ${t("shipper:loadAttributesStep.kilos")}`
                            : EM_DASH}
                        </Text>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <Text size="small" typographyProps={{ color: "textSecondary" }}>
                          {t("common:totalCBM")}
                        </Text>
                        <Text size="small" weight="bold">
                          {quoteRequest.load_spec?.total_volume
                            ? t("common:volume_cbm", { value: quoteRequest.load_spec.total_volume })
                            : EM_DASH}
                        </Text>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <Text size="small" typographyProps={{ color: "textSecondary" }}>
                          {t("common:serviceLevel")}
                        </Text>
                        <Text size="small" weight="bold">
                          {quoteRequest.load_spec?.service_levels
                            ? quoteRequest.load_spec.service_levels
                                .map((serviceLevel) => t(`common:serviceLevelEnum.${serviceLevel}`))
                                .join(", ")
                            : EM_DASH}
                        </Text>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <Text size="small" typographyProps={{ color: "textSecondary" }}>
                          {t("shipper:cargoReadyDate")}
                        </Text>
                        <Text size="small" weight="bold">
                          {quoteRequest.load_spec?.cargo_ready_date
                            ? DateTime.fromISO(quoteRequest.load_spec.cargo_ready_date).toLocaleString({
                                month: "long",
                                weekday: "long",
                                day: "numeric",
                              })
                            : EM_DASH}
                        </Text>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <Text size="small" typographyProps={{ color: "textSecondary" }}>
                          {t("common:customClearance")}
                        </Text>
                        <Text size="small" weight="bold">
                          {quoteRequest.load_spec?.include_customs_clearance === true
                            ? t("common:required")
                            : quoteRequest.load_spec?.include_customs_clearance === false
                            ? t("common:notRequired")
                            : EM_DASH}
                        </Text>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <Text size="small" typographyProps={{ color: "textSecondary" }}>
                          {t("common:insurance")}
                        </Text>
                        <Text size="small" weight="bold">
                          {quoteRequest.insurance_required === true
                            ? t("common:required")
                            : quoteRequest.insurance_required === false
                            ? t("common:notRequired")
                            : EM_DASH}
                        </Text>
                      </div>

                      <div className="flex flex-col space-y-2">
                        <Text size="small" weight="bold">
                          {t("common:cargoDetails")}
                        </Text>
                        <table>
                          <thead className="bg-grey-50 border-b-2 p-2">
                            <tr>
                              <th className="p-2 text-left">
                                <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                  {t("common:item")}
                                </Text>
                              </th>
                              <th className="p-2 text-left">
                                <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                  {t("shipper:dimensions")}
                                </Text>
                              </th>
                              <th className="p-2 text-left">
                                <Text size="small" typographyProps={{ color: "textSecondary" }}>
                                  {t("common:totalWeight")}
                                </Text>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {quoteRequest.load_spec?.air_package_groups?.map((packageGroup) => (
                              <tr>
                                <td className="p-2">
                                  <Text size="small" weight="bold">
                                    {`${packageGroup.item_quantity} x ${capitalize(
                                      packageGroup.packaging_type
                                    ).replaceAll(/_/g, " x ")}`}
                                  </Text>
                                </td>
                                <td className="p-2">
                                  <Text size="small" weight="bold">
                                    {`${packageGroup.width_per_package} x ${packageGroup.length_per_package} x ${
                                      packageGroup.height_per_package
                                    } ${t("common:cm")}`}
                                  </Text>
                                </td>
                                <td className="p-2">
                                  <Text size="small" weight="bold">
                                    {t("common:{{value}}kg", {
                                      value: (packageGroup.weight_per_package ?? 1) * (packageGroup.item_quantity ?? 1),
                                    })}
                                  </Text>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <Text size="small" typographyProps={{ color: "textSecondary" }}>
                          {t("common:commodities")}
                        </Text>
                        <Text size="small" weight="bold">
                          {quoteRequest.load_spec?.commodities
                            ? quoteRequest.load_spec.commodities.split(",").join(", ")
                            : EM_DASH}
                        </Text>
                      </div>

                      <div className="flex flex-col space-y-1">
                        <Text size="small" typographyProps={{ color: "textSecondary" }}>
                          {t("common:attachments")}
                        </Text>
                        <Text size="small" weight="bold">
                          <AttachmentsListContainer showDashOnNoAttachments />
                        </Text>
                      </div>
                    </div>

                    <div className="flex flex-col w-1/2 p-4 space-y-3">
                      <Text size="small" weight="bold">
                        {t("shipper:locations")}
                      </Text>

                      {hasOriginSection && (
                        <div className="flex flex-col space-y-3">
                          <Text size="small" weight="bold" typographyProps={{ color: "primary" }}>
                            {t("shipper:origin")}
                          </Text>
                          {quoteRequest.stops?.[0].address && (
                            <>
                              <Text size="small" weight="bold">
                                {formatAddress.fromSnakeCase(quoteRequest.stops[0].address, "short")}
                              </Text>
                              <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                                {quoteRequest.stops[0].address?.name && (
                                  <>
                                    {quoteRequest.stops[0].address?.name}
                                    <br />
                                  </>
                                )}
                                {formatAddress.fromSnakeCase(quoteRequest.stops[0].address, "long")}
                              </Text>
                              {quoteRequest.stops?.[0].cargo_ready_date && (
                                <div className="flex items-center">
                                  <CalendarToday className="mr-2 text-gray-500" style={{ fontSize: 16 }} />
                                  <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                                    {DateTime.fromISO(quoteRequest.stops[0].cargo_ready_date).toFormat("cccc, MMM d")}
                                  </Text>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      <div className="flex flex-col space-y-3">
                        <Text size="small" weight="bold" typographyProps={{ color: "primary" }}>
                          {t("quoteRequest:originAirport")}
                        </Text>
                        {quoteRequest.stops?.[originAirportIndex].address ? (
                          <>
                            <Text size="small" weight="bold">
                              {quoteRequest.stops[originAirportIndex].address?.airport_name ??
                                quoteRequest.stops[originAirportIndex].address?.airport_iata_code ??
                                formatAddress.fromSnakeCase(quoteRequest.stops[originAirportIndex]?.address, "short")}
                            </Text>
                            <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                              {formatAddress.fromSnakeCase(quoteRequest.stops[originAirportIndex].address, "long")}
                            </Text>
                            {quoteRequest.stops?.[originAirportIndex].cargo_ready_date && !hasOriginSection && (
                              <div className="flex items-center">
                                <CalendarToday className="mr-2 text-gray-500" style={{ fontSize: 16 }} />
                                <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                                  {DateTime.fromISO(
                                    quoteRequest.stops[originAirportIndex].cargo_ready_date || ""
                                  ).toFormat("cccc, MMM d")}
                                </Text>
                              </div>
                            )}
                          </>
                        ) : (
                          <Text size="small" weight="bold">
                            {EM_DASH}
                          </Text>
                        )}
                      </div>
                      <div className="flex flex-col space-y-3">
                        <Text size="small" weight="bold" typographyProps={{ style: { color: portexColor.green500 } }}>
                          {t("quoteRequest:destinationAirport")}
                        </Text>
                        {quoteRequest.stops?.[destinationAirportIndex].address ? (
                          <>
                            <Text size="small" weight="bold">
                              {quoteRequest.stops[destinationAirportIndex].address?.airport_name ??
                                quoteRequest.stops[destinationAirportIndex].address?.airport_iata_code ??
                                formatAddress.fromSnakeCase(
                                  quoteRequest.stops[destinationAirportIndex]?.address,
                                  "short"
                                )}
                            </Text>
                            <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                              {formatAddress.fromSnakeCase(quoteRequest.stops[destinationAirportIndex].address, "long")}
                            </Text>
                            {quoteRequest.stops?.[destinationAirportIndex].target_delivery_date &&
                              !hasDestinationSection && (
                                <div className="flex items-center">
                                  <CalendarToday className="mr-2 text-gray-500" style={{ fontSize: 16 }} />
                                  <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                                    {DateTime.fromISO(
                                      quoteRequest.stops[destinationAirportIndex].target_delivery_date || ""
                                    ).toFormat("cccc, MMM d")}
                                  </Text>
                                </div>
                              )}
                          </>
                        ) : (
                          <Text size="small" weight="bold">
                            {EM_DASH}
                          </Text>
                        )}
                      </div>
                      {hasDestinationSection && (
                        <div className="flex flex-col space-y-3">
                          <Text size="small" weight="bold" typographyProps={{ style: { color: portexColor.green500 } }}>
                            {t("shipper:destination")}
                          </Text>
                          {quoteRequest.stops?.[destinationIndex].address && (
                            <>
                              <Text size="small" weight="bold">
                                {formatAddress.fromSnakeCase(quoteRequest.stops[destinationIndex].address, "short")}
                              </Text>
                              <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                                {quoteRequest.stops[destinationIndex].address?.name && (
                                  <>
                                    {quoteRequest.stops[destinationIndex].address?.name}
                                    <br />
                                  </>
                                )}
                                {formatAddress.fromSnakeCase(quoteRequest.stops[destinationIndex].address, "long")}
                              </Text>
                              {quoteRequest.stops?.[destinationIndex].target_delivery_date && (
                                <div className="flex items-center">
                                  <CalendarToday className="mr-2 text-gray-500" style={{ fontSize: 16 }} />
                                  <Text size="x-small" typographyProps={{ color: "textSecondary" }}>
                                    {DateTime.fromISO(
                                      quoteRequest.stops[destinationIndex].target_delivery_date || ""
                                    ).toFormat("cccc, MMM d")}
                                  </Text>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Paper>
              </Paper>
            </div>
          </div>
        </StepBlock>
      </div>
    </FormProvider>
  );
};

export default ReviewStep;
